import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Info, Plus } from 'lucide-react';
import {
  applySheetRowFieldPatches,
  buildCommerceListingOfferByCatalogMaterialId,
  buildRowsForCommerceSheetConfirm,
  collectSheetPreviewCatalogMaterialIds,
  fetchCommerceCatalogMaterialsForPreviewListing,
  formatSheetRowPriceInputValue,
  formatSheetRowPromotionDiscountInputValue,
  getSheetRowCategoryDisplay,
  getSheetRowDefaultRawName,
  getSheetRowDescriptionSeedFromSource,
  getSheetRowPromotionDiscountSeedFromRow,
  getSheetRowPromotionSeedFromSource,
  parseSheetPriceInputToNumber,
  parseSheetPromotionDiscountInputToNumber,
  readSheetRowCatalogMaterialId,
  readSheetRowPriceValue,
  sheetMergedRowMatchesCommerceListing,
  initRematchMapForPreviewRows,
  previewRowsMissingCompletenessBlock,
  rowMeetsSheetConfirmMinimums,
  rowUsesCatalogDirectConfirm,
  type SheetCategoryOption,
  type ManualMaterialSubmitPayload,
  type SheetConfirmSubmitResult,
  type SheetConfirmSummaryUi,
  type SheetRematchByRowIndex,
  type SheetRematchPick,
  type SheetRowFieldPatch,
} from '../../services/materialCommerceSheetService.ts';
import {
  flattenCategories,
  type ParsedCategory,
} from '../Perfil/PerfilMain/MisMaterialesComercio/commerceMaterialsModel.ts';
import { type CatalogPickOption } from '../Perfil/PerfilMain/MisMaterialesComercio/commerceMaterialsCatalogPick.ts';
import { useDebouncedCatalogMaterialSearch } from '../../hooks/useDebouncedCatalogMaterialSearch.ts';
import { searchItemsToCatalogPicks } from '../../services/catalogMaterialsService.ts';
import formStyles from '../OnboardingV2/ConversationalOnboarding/CommerceOnboarding/CommerceForm.module.scss';
import catalogToastStyles from '../Perfil/PerfilMain/MisMaterialesComercio/CommerceMaterialsCatalog.module.scss';
import styles from './CommerceSheetReviewStep.module.scss';
import {
  COL_COUNT,
  SHEET_REVIEW_TABLE_SCROLL_OFFSET_PX,
  SHEET_REVIEW_UI_PAGE_SIZE,
  defaultSuccessContinueLabel,
} from './CommerceSheetReviewStep/commerceSheetReviewConstants.ts';
import {
  descInputValue,
  descriptionOfferInputValue,
  promotionOfferInputValue,
} from './CommerceSheetReviewStep/commerceSheetReviewStepHelpers.ts';
import {
  isManualAddFormClientValid,
  mergeManualAddMaterialClientFieldErrorsIntoState,
  collectManualAddMaterialClientFieldErrors,
  type ManualAddMaterialFieldErrors,
} from './CommerceSheetReviewStep/commerceManualAddMaterialValidation.ts';
import { CommerceSheetManualAddMaterialModal } from './CommerceSheetReviewStep/CommerceSheetManualAddMaterialModal.tsx';
import { CommerceSheetManualCatalogSection } from './CommerceSheetReviewStep/CommerceSheetManualCatalogSection.tsx';
import { CommerceSheetReviewPublishedSuccess } from './CommerceSheetReviewStep/CommerceSheetReviewPublishedSuccess.tsx';
import { CommerceSheetReviewTableRow } from './CommerceSheetReviewStep/CommerceSheetReviewTableRow.tsx';

interface CommerceSheetReviewStepProps {
  previewRows: unknown[];
  categories: SheetCategoryOption[];
  rematchByRowIndex: SheetRematchByRowIndex;
  rowFieldPatches?: Record<number, SheetRowFieldPatch>;
  onRowFieldPatch?: (rowIndex: number, patch: Partial<SheetRowFieldPatch>) => void;
  excludedByRowIndex?: Record<number, boolean>;
  onExcludedChange?: (rowIndex: number, excluded: boolean) => void;
  onAddManualRow?: (payload: ManualMaterialSubmitPayload) => Promise<{ ok: boolean; message?: string }>;
  onRemovePreviewRow?: (rowIndex: number) => void;
  onRematchChange: (rowIndex: number, patch: Partial<SheetRematchPick>) => void;
  onConfirm: () => Promise<SheetConfirmSubmitResult>;
  onSuccessContinue: () => void;
  onBack: () => void;
  errorText?: string | null;
  successContinueLabel?: string;
  commerceId?: number | null;
  commerceMaterialsCategories?: ParsedCategory[];
  manualCatalogEntry?: boolean;
  onCommerceCatalogEditItem?: (
    itemId: number,
    patch: {
      price: number | null;
      unquoted: boolean;
      valueAddedTax: boolean;
      promotion: string;
      promotionDiscount: number | null;
    }
  ) => Promise<void>;
  onCommerceCatalogDeleteItem?: (itemId: number) => Promise<void>;
  commerceCatalogActionsBusy?: boolean;
}

export const CommerceSheetReviewStep: React.FC<CommerceSheetReviewStepProps> = ({
  previewRows,
  categories,
  rematchByRowIndex,
  rowFieldPatches = {},
  onRowFieldPatch,
  excludedByRowIndex = {},
  onExcludedChange,
  onAddManualRow,
  onRemovePreviewRow,
  onRematchChange,
  onConfirm,
  onSuccessContinue,
  onBack,
  errorText,
  successContinueLabel = defaultSuccessContinueLabel,
  commerceId = null,
  commerceMaterialsCategories: commerceMaterialsCategoriesProp,
  manualCatalogEntry = false,
  onCommerceCatalogEditItem,
  onCommerceCatalogDeleteItem,
  commerceCatalogActionsBusy = false,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');
  const [savedSummary, setSavedSummary] = useState<SheetConfirmSummaryUi | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addModalSubmitting, setAddModalSubmitting] = useState(false);
  const [addModalError, setAddModalError] = useState('');
  const [modCategoryId, setModCategoryId] = useState('');
  const [modRawName, setModRawName] = useState('');
  const [modDesc, setModDesc] = useState('');
  const [modPromo, setModPromo] = useState('');
  const [modDisc, setModDisc] = useState('');
  const [modPrice, setModPrice] = useState('');
  const [modUnit, setModUnit] = useState('');
  const [modUnquoted, setModUnquoted] = useState(true);
  const [modVat, setModVat] = useState(false);
  const [sheetFilterQuery, setSheetFilterQuery] = useState('');
  const [sheetStatusFilter, setSheetStatusFilter] = useState<'all' | 'incomplete' | 'complete' | 'unchanged'>('all');
  const [sheetReviewUIPage, setSheetReviewUIPage] = useState(1);
  const [fetchedMaterialsCategories, setFetchedMaterialsCategories] = useState<ParsedCategory[] | null>(null);
  const unchangedBootstrapRef = useRef<Set<number>>(new Set());
  const manualEmptyAutoModalRef = useRef(false);
  const [catalogMaterialPick, setCatalogMaterialPick] = useState<CatalogPickOption | null>(null);
  const [manualSaveToast, setManualSaveToast] = useState<string | null>(null);
  const [addModalFieldErrors, setAddModalFieldErrors] = useState<ManualAddMaterialFieldErrors>({});
  const [addModalValidationAttempted, setAddModalValidationAttempted] = useState(false);

  const patchField = useCallback(
    (rowIndex: number, patch: Partial<SheetRowFieldPatch>) => {
      onRowFieldPatch?.(rowIndex, patch);
    },
    [onRowFieldPatch]
  );

  const categorySelectOptions = useMemo(
    () => categories.map((c) => ({ value: String(c.id), label: c.name })),
    [categories]
  );

  const manualCatalogFlatRows = useMemo(
    () => flattenCategories(commerceMaterialsCategoriesProp ?? []),
    [commerceMaterialsCategoriesProp]
  );

  const manualCatalogTableEnabled =
    onCommerceCatalogEditItem != null && onCommerceCatalogDeleteItem != null;

  const onModCategoryChange = useCallback((v: string) => {
    setModCategoryId(v);
    setCatalogMaterialPick((prev) => {
      if (!prev) return null;
      return String(prev.categoryId) === v ? prev : null;
    });
  }, []);

  const resetAddModalForm = useCallback(() => {
    setModCategoryId('');
    setModRawName('');
    setModDesc('');
    setModPromo('');
    setModDisc('');
    setModPrice('');
    setModUnit('');
    setModUnquoted(true);
    setModVat(false);
    setAddModalSubmitting(false);
    setAddModalError('');
    setCatalogMaterialPick(null);
    setAddModalFieldErrors({});
    setAddModalValidationAttempted(false);
  }, []);

  const openAddMaterialModal = useCallback(() => {
    resetAddModalForm();
    setAddModalOpen(true);
  }, [resetAddModalForm]);

  const closeAddMaterialModal = useCallback(() => {
    setAddModalOpen(false);
    resetAddModalForm();
  }, [resetAddModalForm]);

  useLayoutEffect(() => {
    if (savedSummary) return;
    if (!manualCatalogEntry || previewRows.length > 0 || !onAddManualRow) {
      manualEmptyAutoModalRef.current = false;
      return;
    }
    if (manualEmptyAutoModalRef.current) return;
    manualEmptyAutoModalRef.current = true;
    openAddMaterialModal();
  }, [manualCatalogEntry, onAddManualRow, openAddMaterialModal, previewRows.length, savedSummary]);

  useEffect(() => {
    if (!addModalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (addModalSubmitting) return;
        closeAddMaterialModal();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [addModalOpen, addModalSubmitting, closeAddMaterialModal]);

  const manualListedMaterialIds = useMemo(
    () =>
      manualCatalogFlatRows
        .map((r) => Number(r.material.id))
        .filter((n) => Number.isFinite(n) && n > 0),
    [manualCatalogFlatRows]
  );

  const {
    items: catalogSearchItems,
    status: catalogSearchStatus,
    error: catalogSearchError,
  } = useDebouncedCatalogMaterialSearch(modRawName, {
    enabled: addModalOpen && !catalogMaterialPick,
    limit: 40,
    excludeIds: manualListedMaterialIds,
    categoryId: modCategoryId || undefined,
  });

  useEffect(() => {
    if (!manualSaveToast) return undefined;
    const id = window.setTimeout(() => setManualSaveToast(null), 4200);
    return () => window.clearTimeout(id);
  }, [manualSaveToast]);

  const globalCatalogMatches = useMemo(() => {
    if (catalogMaterialPick || catalogSearchStatus !== 'ready') return [];
    if (modRawName.trim().length < 2) return [];
    return searchItemsToCatalogPicks(catalogSearchItems);
  }, [catalogMaterialPick, catalogSearchStatus, modRawName, catalogSearchItems]);

  const globalCatalogStatus =
    catalogMaterialPick || modRawName.trim().length < 2
      ? 'idle'
      : catalogSearchStatus === 'loading'
        ? 'loading'
        : catalogSearchStatus === 'error'
          ? 'error'
          : 'ready';

  const globalCatalogFetchError = catalogSearchError;

  const manualAddSaveDisabled = useMemo(() => {
    if (addModalSubmitting) return true;
    if (!addModalValidationAttempted) return false;
    if (
      !isManualAddFormClientValid(modRawName, modCategoryId, modUnit, catalogMaterialPick, modUnquoted, modPrice)
    ) {
      return true;
    }
    return Object.values(addModalFieldErrors).some((v) => Boolean(v?.trim()));
  }, [
    addModalSubmitting,
    addModalValidationAttempted,
    modRawName,
    modCategoryId,
    modUnit,
    catalogMaterialPick,
    modUnquoted,
    modPrice,
    addModalFieldErrors,
  ]);

  useEffect(() => {
    if (!addModalValidationAttempted) return;
    setAddModalFieldErrors((prev) =>
      mergeManualAddMaterialClientFieldErrorsIntoState(
        prev,
        modRawName,
        modCategoryId,
        modUnit,
        catalogMaterialPick,
        modUnquoted,
        modPrice
      )
    );
  }, [
    modRawName,
    modCategoryId,
    modUnit,
    catalogMaterialPick,
    modUnquoted,
    modPrice,
    addModalValidationAttempted,
  ]);

  useEffect(() => {
    setAddModalFieldErrors((p) => {
      if (!p.price) return p;
      const n = { ...p };
      delete n.price;
      return n;
    });
  }, [modPrice]);

  useEffect(() => {
    setAddModalFieldErrors((p) => {
      if (!p.discount) return p;
      const n = { ...p };
      delete n.discount;
      return n;
    });
  }, [modDisc]);

  const submitAddMaterialModal = useCallback(() => {
    if (!onAddManualRow) return;
    const name = modRawName.trim();
    const clientErrs = collectManualAddMaterialClientFieldErrors(
      modRawName,
      modCategoryId,
      modUnit,
      catalogMaterialPick,
      modUnquoted,
      modPrice
    );
    if (Object.keys(clientErrs).length > 0) {
      setAddModalFieldErrors(clientErrs);
      setAddModalValidationAttempted(true);
      setAddModalError('');
      return;
    }
    setAddModalFieldErrors({});
    setAddModalValidationAttempted(false);
    setAddModalError('');
    setAddModalSubmitting(true);
    void (async () => {
      try {
        if (catalogMaterialPick) {
          const materialId = Number(catalogMaterialPick.materialId);
          const userCategoryId = Number(catalogMaterialPick.categoryId);
          if (!Number.isFinite(materialId) || materialId <= 0 || !Number.isFinite(userCategoryId) || userCategoryId <= 0) {
            setAddModalError('Elegí de nuevo un material de la lista o completá los datos a mano.');
            return;
          }
          const effectiveUnquoted = modUnquoted || !modPrice.trim();
          let price: number | null = null;
          if (!effectiveUnquoted && modPrice.trim()) {
            const pn = parseSheetPriceInputToNumber(modPrice.trim());
            if (pn == null) {
              setAddModalFieldErrors((p) => ({ ...p, price: 'Revisá el formato del precio.' }));
              setAddModalValidationAttempted(true);
              setAddModalError('');
              return;
            }
            price = pn;
          }
          let promotionDiscount: number | null = null;
          if (modDisc.trim()) {
            const dn = parseSheetPromotionDiscountInputToNumber(modDisc.trim());
            if (dn == null) {
              setAddModalFieldErrors((p) => ({ ...p, discount: 'Revisá el formato del descuento.' }));
              setAddModalValidationAttempted(true);
              setAddModalError('');
              return;
            }
            promotionDiscount = dn;
          }
          const res = await onAddManualRow({
            kind: 'catalog',
            input: {
              materialId,
              userCategoryId,
              rawName: catalogMaterialPick.materialName.trim() || '—',
              price,
              unquoted: effectiveUnquoted,
              valueAddedTax: effectiveUnquoted ? false : modVat,
              promotion: modPromo.trim() || null,
              promotionDiscount,
            },
          });
          if (!res.ok) {
            setAddModalError((res.message ?? '').trim() || 'No se pudo registrar el material.');
            return;
          }
          if (manualCatalogEntry) {
            setManualSaveToast('Material guardado en tu catálogo.');
          }
          closeAddMaterialModal();
          return;
        }
        const cid = Number(modCategoryId);
        const effectiveUnquoted = modUnquoted || !modPrice.trim();
        const discRaw = modDisc.trim();
        if (discRaw) {
          const dn = parseSheetPromotionDiscountInputToNumber(discRaw);
          if (dn == null) {
            setAddModalFieldErrors((p) => ({ ...p, discount: 'Revisá el formato del descuento.' }));
            setAddModalValidationAttempted(true);
            setAddModalError('');
            return;
          }
        }
        if (!effectiveUnquoted) {
          const priceRaw = modPrice.trim();
          if (priceRaw) {
            const pn = parseSheetPriceInputToNumber(priceRaw);
            if (pn == null) {
              setAddModalFieldErrors((p) => ({ ...p, price: 'Revisá el formato del precio.' }));
              setAddModalValidationAttempted(true);
              setAddModalError('');
              return;
            }
          }
        }
        const initialPatch: Partial<SheetRowFieldPatch> = {};
        if (modDesc.trim()) initialPatch.description = modDesc.trim();
        if (modPromo.trim()) initialPatch.promotion = modPromo.trim();
        if (discRaw) {
          const dn = parseSheetPromotionDiscountInputToNumber(discRaw);
          if (dn != null) initialPatch.promotionDiscount = formatSheetRowPromotionDiscountInputValue(dn);
        }
        if (!effectiveUnquoted) {
          const priceRaw = modPrice.trim();
          if (priceRaw) {
            const pn = parseSheetPriceInputToNumber(priceRaw);
            if (pn != null) initialPatch.price = formatSheetRowPriceInputValue(pn);
          }
        }
        initialPatch.unit = modUnit.trim();
        initialPatch.unquoted = effectiveUnquoted;
        initialPatch.valueAddedTax = effectiveUnquoted ? false : modVat;
        const res = await onAddManualRow({
          kind: 'freeform',
          userCategoryId: cid,
          rawName: name,
          initialPatch,
        });
        if (!res.ok) {
          setAddModalError((res.message ?? '').trim() || 'No se pudo registrar el material.');
          return;
        }
        if (manualCatalogEntry) {
          setManualSaveToast('Material guardado en tu catálogo.');
        }
        closeAddMaterialModal();
      } catch {
        setAddModalError('No se pudo registrar el material.');
      } finally {
        setAddModalSubmitting(false);
      }
    })();
  }, [
    catalogMaterialPick,
    closeAddMaterialModal,
    manualCatalogEntry,
    modCategoryId,
    modDesc,
    modDisc,
    modPrice,
    modPromo,
    modRawName,
    modUnquoted,
    modUnit,
    modVat,
    onAddManualRow,
  ]);

  useEffect(() => {
    previewRows.forEach((row, i) => {
      if (excludedByRowIndex[i]) return;
      if (rowFieldPatches[i]?.description !== undefined) return;
      const d = getSheetRowDescriptionSeedFromSource(row);
      if (d) patchField(i, { description: d });
    });
    previewRows.forEach((row, i) => {
      if (excludedByRowIndex[i]) return;
      if (rowFieldPatches[i]?.promotion !== undefined) return;
      const p = getSheetRowPromotionSeedFromSource(row);
      if (p) patchField(i, { promotion: p });
    });
    previewRows.forEach((row, i) => {
      if (excludedByRowIndex[i]) return;
      if (rowFieldPatches[i]?.promotionDiscount !== undefined) return;
      const pd = getSheetRowPromotionDiscountSeedFromRow(row);
      if (pd) patchField(i, { promotionDiscount: pd });
    });
  }, [previewRows, patchField, rowFieldPatches, excludedByRowIndex]);

  const mergedRows = useMemo(
    () => previewRows.map((r, i) => applySheetRowFieldPatches(r, rowFieldPatches[i])),
    [previewRows, rowFieldPatches]
  );

  useEffect(() => {
    previewRows.forEach((row, i) => {
      if (excludedByRowIndex[i]) return;
      if (rowFieldPatches[i]?.unquoted !== undefined) return;
      const eff = mergedRows[i];
      if (readSheetRowPriceValue(eff) != null) return;
      patchField(i, { unquoted: true, valueAddedTax: false });
    });
  }, [previewRows, mergedRows, rowFieldPatches, patchField, excludedByRowIndex]);

  const initialRematchByRow = useMemo(
    () => initRematchMapForPreviewRows(previewRows),
    [previewRows]
  );

  const previewCatalogMaterialIdKey = useMemo(() => {
    const ids = collectSheetPreviewCatalogMaterialIds(previewRows);
    return Array.from(ids)
      .sort((a, b) => a - b)
      .join(',');
  }, [previewRows]);

  const previewRowsRef = useRef(previewRows);
  previewRowsRef.current = previewRows;

  useEffect(() => {
    if (commerceMaterialsCategoriesProp !== undefined) {
      setFetchedMaterialsCategories(null);
      return;
    }
    if (commerceId == null) {
      setFetchedMaterialsCategories(null);
      return;
    }
    let cancelled = false;
    void fetchCommerceCatalogMaterialsForPreviewListing(commerceId, previewRowsRef.current)
      .then((cats) => {
        if (cancelled) return;
        setFetchedMaterialsCategories(cats);
      })
      .catch(() => {
        if (!cancelled) setFetchedMaterialsCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, [commerceId, commerceMaterialsCategoriesProp, previewCatalogMaterialIdKey]);

  const resolvedMaterialsCategories = useMemo(() => {
    if (commerceMaterialsCategoriesProp !== undefined) return commerceMaterialsCategoriesProp;
    return fetchedMaterialsCategories ?? [];
  }, [commerceMaterialsCategoriesProp, fetchedMaterialsCategories]);

  const offerByCatalogMaterialId = useMemo(
    () => buildCommerceListingOfferByCatalogMaterialId(resolvedMaterialsCategories),
    [resolvedMaterialsCategories]
  );

  const rowMatchesStoredListing = useCallback(
    (eff: unknown) => {
      const mid = readSheetRowCatalogMaterialId(eff);
      if (mid == null || mid <= 0) return false;
      const listing = offerByCatalogMaterialId.get(mid);
      if (listing == null) return false;
      return sheetMergedRowMatchesCommerceListing(eff, listing);
    },
    [offerByCatalogMaterialId]
  );

  const rowHasPublishedCommerceMaterial = useCallback(
    (eff: unknown) => {
      const mid = readSheetRowCatalogMaterialId(eff);
      if (mid == null || mid <= 0) return false;
      return offerByCatalogMaterialId.has(mid);
    },
    [offerByCatalogMaterialId]
  );

  useLayoutEffect(() => {
    const next = new Set<number>();
    previewRows.forEach((row, i) => {
      const mid = readSheetRowCatalogMaterialId(row);
      if (mid == null || mid <= 0) return;
      const listing = offerByCatalogMaterialId.get(mid);
      if (listing == null) return;
      if (sheetMergedRowMatchesCommerceListing(row, listing)) next.add(i);
    });
    unchangedBootstrapRef.current = next;
  }, [previewRows, offerByCatalogMaterialId]);

  useEffect(() => {
    if (!onExcludedChange) return;
    const boot = unchangedBootstrapRef.current;
    mergedRows.forEach((eff, i) => {
      if (!boot.has(i)) return;
      if (!excludedByRowIndex[i]) return;
      const mid = readSheetRowCatalogMaterialId(eff);
      const listing = mid != null ? offerByCatalogMaterialId.get(mid) : undefined;
      if (listing == null || !sheetMergedRowMatchesCommerceListing(eff, listing)) {
        onExcludedChange(i, false);
      }
    });
  }, [mergedRows, excludedByRowIndex, offerByCatalogMaterialId, onExcludedChange]);

  const rowsWithMeta = useMemo(() => {
    return previewRows.map((row, i) => {
      const catalogLinked = rowUsesCatalogDirectConfirm(row);
      const sheetRow =
        row != null && typeof row === 'object' && !Array.isArray(row)
          ? (Number((row as Record<string, unknown>).rowIndex) as number)
          : NaN;
      const displayRow = Number.isFinite(sheetRow) ? sheetRow : i + 1;
      return { row, i, catalogLinked, displayRow };
    });
  }, [previewRows]);

  const normalizedSheetFilter = sheetFilterQuery.trim().toLowerCase();

  const legacyCompletenessBanner = useMemo(
    () => previewRowsMissingCompletenessBlock(previewRows),
    [previewRows]
  );

  const sheetStatusOptionCounts = useMemo(() => {
    let nAll = 0;
    let nIncomplete = 0;
    let nComplete = 0;
    let nUnchanged = 0;
    for (let i = 0; i < previewRows.length; i += 1) {
      const row = previewRows[i];
      const eff = mergedRows[i] ?? row;
      const patch = rowFieldPatches[i];
      const dataComplete = rowMeetsSheetConfirmMinimums(row, i, eff, rematchByRowIndex);
      const matchesStored = rowMatchesStoredListing(eff);
      const hasPublishedMaterial = rowHasPublishedCommerceMaterial(eff);
      if (normalizedSheetFilter) {
        const sheetRowNum =
          row != null && typeof row === 'object' && !Array.isArray(row)
            ? (Number((row as Record<string, unknown>).rowIndex) as number)
            : NaN;
        const displayRowNum = Number.isFinite(sheetRowNum) ? sheetRowNum : i + 1;
        const blob = [
          getSheetRowCategoryDisplay(eff, categories),
          getSheetRowDefaultRawName(eff),
          (rematchByRowIndex[i]?.rawName ?? '').trim(),
          descInputValue(eff, patch),
          descriptionOfferInputValue(eff, patch),
          promotionOfferInputValue(eff, patch),
          String(displayRowNum),
          matchesStored ? 'igual oferta ya cargado' : '',
          hasPublishedMaterial ? 'ya en tus materiales' : '',
        ]
          .join(' ')
          .toLowerCase();
        if (!blob.includes(normalizedSheetFilter)) continue;
      }
      nAll += 1;
      if (!dataComplete) nIncomplete += 1;
      if (dataComplete) nComplete += 1;
      if (matchesStored) nUnchanged += 1;
    }
    return { all: nAll, incomplete: nIncomplete, complete: nComplete, unchanged: nUnchanged };
  }, [
    previewRows,
    mergedRows,
    rematchByRowIndex,
    rowFieldPatches,
    normalizedSheetFilter,
    categories,
    rowMatchesStoredListing,
    rowHasPublishedCommerceMaterial,
  ]);

  const filteredReviewRowIndices = useMemo(() => {
    const out: number[] = [];
    for (let i = 0; i < previewRows.length; i += 1) {
      const row = previewRows[i];
      const eff = mergedRows[i] ?? row;
      const patch = rowFieldPatches[i];
      const dataComplete = rowMeetsSheetConfirmMinimums(row, i, eff, rematchByRowIndex);
      const matchesStored = rowMatchesStoredListing(eff);
      const hasPublishedMaterial = rowHasPublishedCommerceMaterial(eff);
      if (sheetStatusFilter === 'incomplete' && dataComplete) continue;
      if (sheetStatusFilter === 'complete' && !dataComplete) continue;
      if (sheetStatusFilter === 'unchanged' && !matchesStored) continue;
      if (normalizedSheetFilter) {
        const sheetRowNum =
          row != null && typeof row === 'object' && !Array.isArray(row)
            ? (Number((row as Record<string, unknown>).rowIndex) as number)
            : NaN;
        const displayRowNum = Number.isFinite(sheetRowNum) ? sheetRowNum : i + 1;
        const blob = [
          getSheetRowCategoryDisplay(eff, categories),
          getSheetRowDefaultRawName(eff),
          (rematchByRowIndex[i]?.rawName ?? '').trim(),
          descInputValue(eff, patch),
          descriptionOfferInputValue(eff, patch),
          promotionOfferInputValue(eff, patch),
          String(displayRowNum),
          matchesStored ? 'igual oferta ya cargado' : '',
          hasPublishedMaterial ? 'ya en tus materiales' : '',
        ]
          .join(' ')
          .toLowerCase();
        if (!blob.includes(normalizedSheetFilter)) continue;
      }
      out.push(i);
    }
    return out;
  }, [
    previewRows,
    mergedRows,
    rematchByRowIndex,
    rowFieldPatches,
    sheetStatusFilter,
    normalizedSheetFilter,
    categories,
    rowMatchesStoredListing,
    rowHasPublishedCommerceMaterial,
  ]);

  const sheetReviewTotalPages = Math.max(
    1,
    Math.ceil(filteredReviewRowIndices.length / SHEET_REVIEW_UI_PAGE_SIZE)
  );

  useEffect(() => {
    setSheetReviewUIPage((p) => Math.min(Math.max(1, p), sheetReviewTotalPages));
  }, [sheetReviewTotalPages]);

  useEffect(() => {
    setSheetReviewUIPage(1);
  }, [sheetFilterQuery, sheetStatusFilter, previewRows.length]);

  const pagedReviewRowIndices = useMemo(() => {
    const start = (sheetReviewUIPage - 1) * SHEET_REVIEW_UI_PAGE_SIZE;
    return filteredReviewRowIndices.slice(start, start + SHEET_REVIEW_UI_PAGE_SIZE);
  }, [filteredReviewRowIndices, sheetReviewUIPage]);

  const sheetReviewVisibleRangeLabel = useMemo(() => {
    if (filteredReviewRowIndices.length === 0) return '0 resultados con el filtro actual';
    const from = (sheetReviewUIPage - 1) * SHEET_REVIEW_UI_PAGE_SIZE + 1;
    const to = Math.min(sheetReviewUIPage * SHEET_REVIEW_UI_PAGE_SIZE, filteredReviewRowIndices.length);
    return `Filas ${from}–${to} de ${filteredReviewRowIndices.length} con el filtro actual (${previewRows.length} en la hoja)`;
  }, [filteredReviewRowIndices.length, previewRows.length, sheetReviewUIPage]);

  const sheetReviewTableRef = useRef<HTMLDivElement>(null);

  const onSheetReviewPageNav = useCallback(
    (direction: 'prev' | 'next') => {
      setSheetReviewUIPage((p) => {
        if (direction === 'prev') return Math.max(1, p - 1);
        return Math.min(sheetReviewTotalPages, p + 1);
      });
      window.setTimeout(() => {
        const el = sheetReviewTableRef.current;
        if (!el) return;
        const top = el.getBoundingClientRect().top + window.scrollY - SHEET_REVIEW_TABLE_SCROLL_OFFSET_PX;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      }, 0);
    },
    [sheetReviewTotalPages]
  );

  const sheetConfirmPreflight = useMemo(
    () => buildRowsForCommerceSheetConfirm(mergedRows, rematchByRowIndex, excludedByRowIndex),
    [mergedRows, rematchByRowIndex, excludedByRowIndex]
  );

  const pendingBannerText = useMemo(() => {
    if (previewRows.length === 0) return null;
    return sheetConfirmPreflight.error;
  }, [previewRows.length, sheetConfirmPreflight.error]);

  const publishBlocked = previewRows.length > 0 && sheetConfirmPreflight.error != null;
  const canPublishSheet = previewRows.length > 0;

  const displayError = (errorText ?? '').trim() || localError;
  const manualOnlyEmpty = manualCatalogEntry && previewRows.length === 0;

  const renderSheetReviewPagination = () => {
    if (sheetReviewTotalPages <= 1) return null;
    return (
      <footer
        className={`${styles.sheetPagination} ${styles.sheetPaginationBottom}`}
        aria-label="Paginación del listado"
      >
        <button type="button" disabled={sheetReviewUIPage <= 1} onClick={() => onSheetReviewPageNav('prev')}>
          Anterior
        </button>
        <span>
          Página {sheetReviewUIPage} de {sheetReviewTotalPages}
        </span>
        <button
          type="button"
          disabled={sheetReviewUIPage >= sheetReviewTotalPages}
          onClick={() => onSheetReviewPageNav('next')}
        >
          Siguiente
        </button>
      </footer>
    );
  };

  if (savedSummary) {
    return (
      <CommerceSheetReviewPublishedSuccess
        savedSummary={savedSummary}
        successContinueLabel={successContinueLabel}
        onSuccessContinue={onSuccessContinue}
      />
    );
  }

  return (
    <div
      className={`${styles.wrap} ${manualOnlyEmpty ? '' : styles.wrapReviewDocked}`.trim()}
    >
      <div className={styles.reviewScrollBlock}>
        {onAddManualRow ? (
          <CommerceSheetManualAddMaterialModal
            addModalOpen={addModalOpen}
            closeAddMaterialModal={closeAddMaterialModal}
            addModalSubmitting={addModalSubmitting}
            submitAddMaterialModal={submitAddMaterialModal}
            addModalError={addModalError}
            globalCatalogFetchError={globalCatalogFetchError}
            categorySelectOptions={categorySelectOptions}
            modCategoryId={modCategoryId}
            onModCategoryChange={onModCategoryChange}
            modRawName={modRawName}
            setModRawName={setModRawName}
            catalogMaterialPick={catalogMaterialPick}
            setCatalogMaterialPick={setCatalogMaterialPick}
            setAddModalError={setAddModalError}
            globalCatalogStatus={globalCatalogStatus}
            globalCatalogMatches={globalCatalogMatches}
            modDesc={modDesc}
            setModDesc={setModDesc}
            modPromo={modPromo}
            setModPromo={setModPromo}
            modDisc={modDisc}
            setModDisc={setModDisc}
            modPrice={modPrice}
            setModPrice={setModPrice}
            modUnit={modUnit}
            setModUnit={setModUnit}
            modUnquoted={modUnquoted}
            setModUnquoted={setModUnquoted}
            modVat={modVat}
            setModVat={setModVat}
            setModCategoryId={setModCategoryId}
            fieldErrors={addModalFieldErrors}
            saveButtonDisabled={manualAddSaveDisabled}
          />
        ) : null}
        {manualOnlyEmpty ? (
          <CommerceSheetManualCatalogSection
            displayError={displayError}
            addModalOpen={addModalOpen}
            manualCatalogFlatRows={manualCatalogFlatRows}
            manualCatalogTableEnabled={manualCatalogTableEnabled}
            onCommerceCatalogEditItem={onCommerceCatalogEditItem}
            onCommerceCatalogDeleteItem={onCommerceCatalogDeleteItem}
            commerceCatalogActionsBusy={commerceCatalogActionsBusy ?? false}
            addModalSubmitting={addModalSubmitting}
            openAddMaterialModal={openAddMaterialModal}
          />
        ) : (
          <>
            <h2 className={styles.title}>Revisión antes de guardar</h2>
            <p className={styles.intro}>
              {previewRows.length > 0
                ? 'Mirá cómo quedó tu listado y ajustá lo que haga falta. Cuando esté bien para vos, publicalo en tu perfil.'
                : 'Agregá materiales de a uno: cada material quedará guardado en tu comercio. Si más adelante querés cargar una planilla, volvé atrás y subí un archivo.'}
            </p>
      {legacyCompletenessBanner ? (
        <div className={styles.legacyCompletenessBanner} role="note">
          <Info size={16} aria-hidden />
          <span>
            Este borrador no incluye el bloque de completitud del análisis. Lo ideal es volver a subir o analizar el
            archivo para ver el estado de cada fila. Si seguís acá, revisá nombre, categoría y unidad en las filas que
            sigan incompletas.
          </span>
        </div>
      ) : null}
      {pendingBannerText != null ? (
        <div className={styles.sheetTopBar}>
          <p className={styles.pendingBanner} role="status">
            <Info size={16} />
            {pendingBannerText}
          </p>
        </div>
      ) : null}
      {displayError ? <p className={styles.error}>{displayError}</p> : null}
      {previewRows.length > 0 ? (
        <div className={styles.sheetFilterBar}>
          <div className={styles.sheetFilterField}>
            <label className={styles.sheetFilterLabel} htmlFor="sheet-review-filter-q">
              Buscar en el listado
            </label>
            <input
              id="sheet-review-filter-q"
              type="search"
              className={styles.sheetFilterInput}
              value={sheetFilterQuery}
              onChange={(e) => setSheetFilterQuery(e.target.value)}
              placeholder="Nombre, categoría, descripción…"
              autoComplete="off"
            />
          </div>
          <div className={styles.sheetFilterStatus}>
            <label className={styles.sheetFilterLabel} htmlFor="sheet-review-filter-status">
              Estado
            </label>
            <select
              id="sheet-review-filter-status"
              className={`${formStyles.selectMuted} ${styles.sheetFilterStatusSelect}`}
              value={sheetStatusFilter}
              onChange={(e) =>
                setSheetStatusFilter(e.target.value as 'all' | 'incomplete' | 'complete' | 'unchanged')
              }
            >
              <option value="all">Todos ({sheetStatusOptionCounts.all})</option>
              <option value="incomplete">Falta completar ({sheetStatusOptionCounts.incomplete})</option>
              <option value="complete">Listos ({sheetStatusOptionCounts.complete})</option>
              <option value="unchanged">Igual a tu listado (sin cambios) ({sheetStatusOptionCounts.unchanged})</option>
            </select>
          </div>
          {onAddManualRow ? (
            <div className={styles.sheetFilterAddWrap}>
              <button type="button" className={styles.addMaterialButton} onClick={openAddMaterialModal}>
                <Plus size={16} />
                Agregar material al catálogo
              </button>
            </div>
          ) : null}
          <p className={styles.sheetFilterCount} aria-live="polite">
            {sheetReviewVisibleRangeLabel}
          </p>
        </div>
      ) : onAddManualRow ? (
        <div className={styles.sheetFilterBar}>
          <p className={styles.sheetFilterCount} style={{ flex: '1 1 100%', margin: 0 }}>
            No hay filas de planilla en esta pantalla. Usá el botón para dar de alta un material; cada guardado queda
            aplicado al instante.
          </p>
          <div className={styles.sheetFilterAddWrap}>
            <button type="button" className={styles.addMaterialButton} onClick={openAddMaterialModal}>
              <Plus size={16} />
              Agregar material al catálogo
            </button>
          </div>
        </div>
      ) : null}
      <div ref={sheetReviewTableRef} className={styles.tableWrap}>
        <table className={styles.table}>
          <colgroup>
            <col className={styles.colItem} />
            <col className={styles.colEstado} />
            <col className={styles.colCategoria} />
            <col className={styles.colDetalle} />
            <col className={styles.colPrecio} />
          </colgroup>
          <thead>
            <tr>
              <th className={`${styles.th} ${styles.thItem}`} scope="col">
                Ítem
              </th>
              <th className={styles.th}>Estado</th>
              <th className={styles.th}>Categoría</th>
              <th className={styles.th}>Detalle</th>
              <th className={styles.th} scope="col" aria-describedby="sheet-price-footnote">
                Precio de lista<sup className={styles.thAsterisk}>*</sup>
              </th>
            </tr>
          </thead>
          <tbody>
            {previewRows.length === 0 && onAddManualRow ? (
              <tr>
                <td colSpan={COL_COUNT} className={styles.sheetFilterEmpty}>
                  Todavía no hay filas de archivo para revisar. Usá «Agregar material al catálogo» arriba para cargar
                  productos de a uno.
                </td>
              </tr>
            ) : null}
            {previewRows.length > 0 && filteredReviewRowIndices.length === 0 ? (
              <tr>
                <td colSpan={COL_COUNT} className={styles.sheetFilterEmpty}>
                  Ningún ítem coincide con el filtro. Probá otra búsqueda o cambiá el estado.
                </td>
              </tr>
            ) : null}
            {pagedReviewRowIndices.map((i) => {
              const meta = rowsWithMeta[i];
              if (!meta) return null;
              const { row, catalogLinked, displayRow } = meta;
              const eff = mergedRows[i] ?? row;
              const patch = rowFieldPatches[i];
              const remPick = rematchByRowIndex[i];
              const remInit = initialRematchByRow[i] ?? {
                userCategoryId: '' as const,
                rawName: '',
                createCatalogMaterial: false,
                rejectSuggestedCatalogMatch: false,
              };
              const remCur = rematchByRowIndex[i] ?? {
                userCategoryId: '' as const,
                rawName: '',
                createCatalogMaterial: false,
                rejectSuggestedCatalogMatch: false,
              };
              return (
                <CommerceSheetReviewTableRow
                  key={i}
                  rowIndex={i}
                  row={row}
                  eff={eff}
                  patch={patch}
                  displayRow={displayRow}
                  catalogLinked={catalogLinked}
                  remPick={remPick}
                  remInit={remInit}
                  remCur={remCur}
                  categories={categories}
                  categorySelectOptions={categorySelectOptions}
                  excludedByRowIndex={excludedByRowIndex}
                  rematchByRowIndex={rematchByRowIndex}
                  patchField={patchField}
                  onRematchChange={onRematchChange}
                  onExcludedChange={onExcludedChange}
                  onRemovePreviewRow={onRemovePreviewRow}
                  rowMatchesStoredListing={rowMatchesStoredListing}
                  rowHasPublishedCommerceMaterial={rowHasPublishedCommerceMaterial}
                  unchangedBootstrapRef={unchangedBootstrapRef}
                />
              );
            })}
          </tbody>
        </table>
      </div>
      <p id="sheet-price-footnote" className={styles.tableFootnote}>
        <span className={styles.footnoteMark}>*</span> Los precios que figuren en la tabla son meramente informativos
        y optativos. Edify no los presenta como oferta real ni como compromiso comercial por parte del comercio.
      </p>
      {renderSheetReviewPagination()}
          </>
        )}
      </div>
      {!manualOnlyEmpty ? (
        <div className={styles.reviewActionsDock} role="region" aria-label="Publicar en el perfil">
        <div className={styles.reviewActionsDockInner}>
          <div className={styles.reviewActionsDockText}>
            <p className={styles.reviewActionsDockTitle}>
              {canPublishSheet ? 'Listo para publicar' : 'Publicar planilla'}
            </p>
            <p className={styles.reviewActionsDockHint}>
              {canPublishSheet
                ? 'Al publicar se guarda el listado completo (todas las filas y páginas), con los cambios que hiciste en la tabla.'
                : 'Solo aplica cuando hay filas provenientes de un archivo o borrador. Los materiales que agregás con el formulario ya quedaron guardados al instante.'}
            </p>
          </div>
          <div className={styles.reviewActionsGroup}>
            <button
              type="button"
              className={`${formStyles.submit} ${styles.reviewActionsPrimary}`}
              disabled={submitting || publishBlocked || !canPublishSheet}
              aria-busy={submitting}
              onClick={() => {
                setLocalError('');
                setSubmitting(true);
                void (async () => {
                  try {
                    const result = await onConfirm();
                    if (result.ok) {
                      setSavedSummary(result.summary);
                    } else if (result.ok === false) {
                      const err = result.message.trim();
                      if (err) setLocalError(err);
                    }
                  } finally {
                    setSubmitting(false);
                  }
                })();
              }}
            >
              {submitting ? (
                <span className={styles.confirmWithSpinner}>
                  <span className={styles.spinner} aria-hidden />
                  Guardando…
                </span>
              ) : (
                'Publicar listado en mi perfil'
              )}
            </button>
            <button type="button" className={formStyles.cancel} disabled={submitting} onClick={onBack}>
              Volver
            </button>
          </div>
        </div>
      </div>
      ) : null}
      {manualSaveToast ? (
        <div
          role="status"
          className={`${catalogToastStyles.catalogToast} ${catalogToastStyles.catalogToastSuccess}`}
        >
          {manualSaveToast}
        </div>
      ) : null}
    </div>
  );
};

export default CommerceSheetReviewStep;
